// The Breeze panel inside a call: notes, questions, and next steps the whole
// room shares, and a Mine tab for what only you keep. A host also gets a Host
// tab: the switch for Breeze's live assistant, the agenda, and the line to say
// next. Nothing on the Host tab or in Mine is ever rendered while the host
// shares their screen.
//
// Docked at the right on a desktop-sized window, a bottom sheet elsewhere. The
// private tabs collapse while you share your screen, so a full-screen share
// never carries your goal or notes into the room.

import { useEffect, useId, useRef, useState, type FormEvent, type JSX } from "react";
import { useSwipe } from "../hooks/useSwipe";
import { ASK, ASSISTANT, BREEZE_ID, PANEL_LIMITS, type HelpMatch, type PanelItem, type PanelList, type PrivateData, type ShareSurface } from "@breeze/core";
import type { AssistantStore } from "../hooks/useAssistant";
import type { PanelStore } from "../hooks/usePanel";
import { BreezeMarkIcon, CloseIcon, TrashIcon } from "./icons";
import { TextBox } from "./TextBox";
import { inline } from "../lib/richText";

/** One line of kept text with its bold and highlights shown. `inline` escapes
 * the text before it adds any tag. */
function RichLine({ text }: { text: string }): JSX.Element {
  return <span dangerouslySetInnerHTML={{ __html: inline(text) }} />;
}

type TabId = "shared" | "mine" | "host";

interface BreezePanelProps {
  readonly panel: PanelStore;
  readonly assistant: AssistantStore;
  readonly selfId: string | null;
  readonly isHost: boolean;
  /** A screen share that could capture this panel is on: private content
   * stays collapsed. A share of another window or tab cannot, so the caller
   * passes false for it (QUEUE row 443). */
  readonly sharingScreen: boolean;
  /** What the running share captures, for the line shown in place of the
   * notes; null when no share is running or the caller does not know. */
  readonly shareSurface?: ShareSurface | null;
  /** "dock" on a desktop-sized window, "top" (the phone strip opened out) on a
   * phone, "sheet" for the bottom sheet kept for any caller that wants it. */
  readonly layout: "dock" | "sheet" | "top";
  /** Shared items only, with no tab bar: a call on a phone (see BreezeStrip). */
  readonly publicOnly?: boolean;
  /** Whether the call is being recorded; absent where that does not apply
   * (Listen). While it is false the panel says so at the top. */
  readonly recording?: RecordingShown;
  readonly onClose: () => void;
  /** "top" only: back to the strip. A swipe up on the handle or a tap on it. */
  readonly onCollapse?: () => void;
  /** The host's goals and opening question written from the prep open beside
   * the panel, for while the live assistant has not written its own. */
  readonly prepGoals?: PrepGoals | null;
}

export interface PrepGoals {
  readonly goal: string;
  readonly opener: string;
}

/** The private notes as plain text, for the clipboard. */
export function notesAsText(data: PrivateData): string {
  const parts: string[] = [];
  if (data.goal.trim()) parts.push(`Goal: ${data.goal.trim()}`);
  if (data.notes.trim()) parts.push(`Notes:\n${data.notes.trim()}`);
  if (data.questions.length > 0) {
    parts.push(`Questions:\n${data.questions.map((q) => `${q.done ? "[x]" : "[ ]"} ${q.text}`).join("\n")}`);
  }
  return parts.join("\n\n");
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand("copy");
      area.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export function BreezePanel({ panel, assistant, selfId, isHost, sharingScreen, shareSurface = null, layout, publicOnly = false, recording, onClose, onCollapse, prepGoals = null }: BreezePanelProps): JSX.Element {
  const [tab, setTab] = useState<TabId>("mine");
  const rootRef = useRef<HTMLElement>(null);
  const baseId = useId();

  // Sharing the screen while on the Host tab would leave it showing a tab the
  // bar below has just hidden: land on Shared instead.
  const activeTab: TabId = publicOnly ? "shared" : tab === "host" && (!isHost || sharingScreen) ? "shared" : tab;

  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const tabs: { id: TabId; label: string }[] = [
    { id: "mine", label: "Mine" },
    { id: "shared", label: "Shared" },
  ];
  if (isHost && !sharingScreen) tabs.push({ id: "host", label: "Host" });

  return (
    <aside
      ref={rootRef}
      className={`bp bp--${layout}`}
      aria-label="Breeze"
      data-testid="breeze-panel"
      tabIndex={-1}
    >
      <header className="bp__head">
        <span className="bp__mark">
          <BreezeMarkIcon size={22} />
        </span>
        <h2 className="bp__title">Breeze</h2>
        <button type="button" className="bp__close" aria-label="Close Breeze" onClick={onClose}>
          <CloseIcon size={22} />
        </button>
      </header>

      {publicOnly ? null : (
      <div className="bp__tabs" role="tablist" aria-label="Breeze">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`${baseId}-tab-${t.id}`}
            aria-selected={activeTab === t.id}
            aria-controls={`${baseId}-panel`}
            className="bp__tab"
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      )}

      {recording === "armed" ? (
        <div className="bp__minenote bp__armed" role="status" data-testid="recording-armed-banner">
          <span>Recording starts when someone joins</span>
        </div>
      ) : null}

      {panel.problem || assistant.problem ? (
        <div className="bp__problem" role="alert">
          {panel.problem ?? assistant.problem}
        </div>
      ) : null}

      <div className="bp__body" role={publicOnly ? undefined : "tabpanel"} id={`${baseId}-panel`} aria-labelledby={publicOnly ? undefined : `${baseId}-tab-${activeTab}`}>
        {activeTab === "shared" ? (
          !isHost && assistant.state?.guestNotes !== true ? (
            <p className="bp__empty bp__empty--block" data-testid="shared-off">Shared notes are off for this call.</p>
          ) : (
            <SharedTab panel={panel} selfId={selfId} isHost={isHost} />
          )
        ) : activeTab === "mine" ? (
          sharingScreen ? (
            <Collapsed surface={shareSurface} />
          ) : (
            <MineTab panel={panel} assistant={assistant} isHost={isHost} prepGoals={prepGoals} />
          )
        ) : sharingScreen ? (
          <Collapsed surface={shareSurface} />
        ) : (
          <HostTab assistant={assistant} />
        )}
      </div>
      {layout === "top" && onCollapse ? <TopHandle onCollapse={onCollapse} /> : null}
    </aside>
  );
}

/** On, off, or "armed": the owner joined with Record on and it starts once a
 * second person is in. Adam, 2026-10-04, alone in his room after joining with
 * Record on: "i joined with recording turned on and this still appears off". */
export type RecordingShown = boolean | "armed";

// Adam, 2026-10-04: "i want a notice above their mine sections saying what you
// note here stays private to you", then "make that xable and only appear the
// first time they go in". A guest's first Mine tab ever shows it; it stays for
// the rest of that page until closed, and never comes back after.
const MINE_NOTICE_KEY = "breeze:mine-private-seen";
let mineNoticeThisPage: boolean | null = null;

function mineNoticeShown(): boolean {
  if (mineNoticeThisPage !== null) return mineNoticeThisPage;
  let seen = false;
  try {
    seen = localStorage.getItem(MINE_NOTICE_KEY) === "1";
    if (!seen) localStorage.setItem(MINE_NOTICE_KEY, "1");
  } catch {
    // Storage blocked: show it this page only.
  }
  mineNoticeThisPage = !seen;
  return mineNoticeThisPage;
}

function MinePrivateNotice(): JSX.Element | null {
  const [shown, setShown] = useState(mineNoticeShown);
  if (!shown) return null;
  return (
    <div className="bp__minenote" data-testid="mine-private-notice">
      <span>What you note here stays private to you.</span>
      <button
        type="button"
        className="bp__minenote-x"
        aria-label="Close"
        onClick={() => {
          mineNoticeThisPage = false;
          setShown(false);
        }}
      >
        <CloseIcon size={16} />
      </button>
    </div>
  );
}

/** The grabber under the opened-out strip: swipe up or tap to fold it back. */
function TopHandle({ onCollapse }: { readonly onCollapse: () => void }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useSwipe(ref, {
    follow: ["up"],
    onTap: onCollapse,
    onSwipe: (d) => {
      if (d === "up") onCollapse();
    },
  });
  return (
    <div ref={ref} className="bp__grab" role="button" tabIndex={0} aria-label="Fold Breeze back up" data-testid="panel-grab"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCollapse();
        }
      }}
    >
      <span className="bp__grab-bar" aria-hidden="true" />
    </div>
  );
}

function Collapsed({ surface }: { surface: ShareSurface | null }): JSX.Element {
  const what = surface === "monitor" ? "your entire screen" : "your screen";
  return <p className="bp__empty bp__empty--block">Hidden while you share {what}. Share a window or tab instead to see your notes.</p>;
}

// ---- Shared ----------------------------------------------------------------

function SharedTab({ panel, selfId, isHost }: { panel: PanelStore; selfId: string | null; isHost: boolean }): JSX.Element {
  const shared = panel.shared;
  if (!shared) {
    return (
      <div className="bp__loading" role="status" aria-label="Loading">
        <span />
        <span />
        <span />
      </div>
    );
  }
  return (
    <>
      <Section title="Notes" list="notes" items={shared.notes} empty="No notes yet." panel={panel} selfId={selfId} isHost={isHost} />
      <Section
        title="Questions"
        list="questions"
        items={[...shared.questions.filter((q) => !q.asked), ...shared.questions.filter((q) => q.asked)]}
        empty="No questions yet."
        panel={panel}
        selfId={selfId}
        isHost={isHost}
      />
      <Section title="Next steps" list="steps" items={shared.steps} empty="No next steps yet." panel={panel} selfId={selfId} isHost={isHost} />
    </>
  );
}

interface SectionProps {
  readonly title: string;
  readonly list: PanelList;
  readonly items: readonly PanelItem[];
  readonly empty: string;
  readonly panel: PanelStore;
  readonly selfId: string | null;
  readonly isHost: boolean;
}

function Section({ title, list, items, empty, panel, selfId, isHost }: SectionProps): JSX.Element {
  const [text, setText] = useState("");
  const [owner, setOwner] = useState("");
  const headingId = useId();
  const add = (): void => {
    if (!text.trim()) return;
    panel.addItem(list, text, list === "steps" ? { owner } : undefined);
    setText("");
    setOwner("");
  };
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    add();
  };
  return (
    <section className="bp__section" aria-labelledby={headingId}>
      <h3 id={headingId} className="bp__heading">
        {title}
      </h3>
      {items.length === 0 ? (
        <p className="bp__empty">{empty}</p>
      ) : (
        <ul className="bp__list" aria-label={title}>
          {items.map((item) => (
            <ItemRow key={item.id} list={list} item={item} mayEdit={isHost || item.authorId === selfId} isHost={isHost} panel={panel} />
          ))}
        </ul>
      )}
      <form className="bp__add" onSubmit={submit}>
        <TextBox
          className="bp__input"
          value={text}
          maxLength={PANEL_LIMITS.itemChars}
          onValue={setText}
          onEnter={add}
          singleLine
          ariaLabel={list === "notes" ? "Add a note" : list === "questions" ? "Add a question" : "Add a next step"}
          placeholder={list === "notes" ? "Add a note" : list === "questions" ? "Add a question" : "Add a next step"}
        />
        {list === "steps" ? (
          <input
            className="bp__input bp__input--owner"
            value={owner}
            maxLength={PANEL_LIMITS.ownerChars}
            onChange={(e) => setOwner(e.target.value)}
            aria-label="Owner"
            placeholder="Owner"
            autoComplete="off"
          />
        ) : null}
        <button type="submit" className="bp__btn bp__btn--accent" disabled={!text.trim()}>
          Add
        </button>
      </form>
    </section>
  );
}

interface ItemRowProps {
  readonly list: PanelList;
  readonly item: PanelItem;
  readonly mayEdit: boolean;
  readonly isHost: boolean;
  readonly panel: PanelStore;
}

function ItemRow({ list, item, mayEdit, isHost, panel }: ItemRowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [owner, setOwner] = useState(item.owner ?? "");

  const startEdit = (): void => {
    setText(item.text);
    setOwner(item.owner ?? "");
    setEditing(true);
  };
  const commit = (): void => {
    const patch: { text?: string; owner?: string } = {};
    if (text.trim() && text.trim() !== item.text) patch.text = text.trim();
    if (list === "steps" && owner.trim() !== (item.owner ?? "")) patch.owner = owner.trim();
    if (patch.text !== undefined || patch.owner !== undefined) panel.editItem(list, item.id, patch);
    setEditing(false);
  };
  const save = (e: FormEvent): void => {
    e.preventDefault();
    commit();
  };

  if (editing) {
    return (
      <li className="bp__item bp__item--editing">
        <form className="bp__edit" onSubmit={save} onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), setEditing(false))}>
          <TextBox
            className="bp__input"
            value={text}
            maxLength={PANEL_LIMITS.itemChars}
            onValue={setText}
            onEnter={commit}
            onEscape={() => setEditing(false)}
            singleLine
            ariaLabel="Text"
            autoFocus
          />
          {list === "steps" ? (
            <input
              className="bp__input"
              value={owner}
              maxLength={PANEL_LIMITS.ownerChars}
              onChange={(e) => setOwner(e.target.value)}
              aria-label="Owner"
              placeholder="Owner"
            />
          ) : null}
          <div className="bp__edit-actions">
            <button type="button" className="bp__btn" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="submit" className="bp__btn bp__btn--accent" disabled={!text.trim()}>
              Save
            </button>
          </div>
        </form>
      </li>
    );
  }

  const asked = list === "questions" && item.asked === true;
  const done = list === "steps" && item.done === true;
  return (
    <li className={`bp__item${asked ? " bp__item--asked" : ""}${done ? " bp__item--done" : ""}`} data-list={list}>
      {list === "steps" ? (
        <label className="bp__check">
          <input
            type="checkbox"
            checked={done}
            disabled={!mayEdit}
            onChange={(e) => panel.editItem(list, item.id, { done: e.target.checked })}
            aria-label={`Done: ${item.text}`}
          />
          <span className="bp__box" aria-hidden="true" />
        </label>
      ) : null}
      <div className="bp__main">
        {mayEdit ? (
          <button type="button" className="bp__text bp__text--edit" onClick={startEdit} aria-label={`Edit: ${item.text}`}>
            <RichLine text={item.text} />
          </button>
        ) : (
          <span className="bp__text">
            <RichLine text={item.text} />
          </span>
        )}
        <span className="bp__meta">
          {item.goal ? <span className="bp__tag">Goal</span> : null}
          {item.authorId === BREEZE_ID ? <span className="bp__tag bp__tag--breeze">Added by Breeze</span> : item.authorName}
          {list === "steps" && item.owner ? <span> · For {item.owner}</span> : null}
          {asked ? <span className="bp__tag bp__tag--quiet">Asked</span> : null}
        </span>
      </div>
      {list === "questions" && isHost ? (
        <button
          type="button"
          className="bp__btn bp__btn--ghost"
          aria-pressed={asked}
          onClick={() => panel.editItem(list, item.id, { asked: !asked })}
        >
          {asked ? "Undo" : "Asked"}
        </button>
      ) : null}
      {mayEdit ? (
        <button type="button" className="bp__icon" aria-label={`Delete: ${item.text}`} onClick={() => panel.removeItem(list, item.id)}>
          <TrashIcon size={19} />
        </button>
      ) : null}
    </li>
  );
}

// ---- Mine ------------------------------------------------------------------

function MineTab({ panel, assistant, isHost, prepGoals }: { panel: PanelStore; assistant: AssistantStore; isHost: boolean; prepGoals: PrepGoals | null }): JSX.Element {
  const { priv } = panel;
  const { state, host } = assistant;
  const [copied, setCopied] = useState<"mine" | "failed" | null>(null);
  const goalId = useId();
  const notesId = useId();
  // Only the host has a goal (Adam, 2026-10-18: "take what would you like to
  // get out of the call off their surfaces entirely"). Guests get a chat with
  // Breeze while the host leaves it on; the host's chat is the prep workspace's.
  const showGoal = isHost;
  const showChat = !isHost && state?.on === true && state.guestChat;

  // The host's goal fills in from the prep once, while the box is empty. A goal
  // the host cleared is not put back.
  const filledGoal = useRef<string | null>(null);
  const prepGoal = isHost ? host?.goal || prepGoals?.goal || "" : "";
  useEffect(() => {
    if (!prepGoal || filledGoal.current === prepGoal) return;
    filledGoal.current = prepGoal;
    if (!panel.priv.goal.trim()) panel.setGoal(prepGoal);
  }, [prepGoal, panel]);

  const copy = async (which: "mine", data: PrivateData): Promise<void> => {
    const ok = await copyText(notesAsText(data));
    setCopied(ok ? which : "failed");
    setTimeout(() => setCopied(null), 2200);
  };

  return (
    <>
      {isHost ? null : <MinePrivateNotice />}

      {showGoal ? (
        <section className="bp__section">
          <label className="bp__heading" htmlFor={goalId}>
            Goal for this call
          </label>
          <TextBox
            id={goalId}
            className="bp__textarea"
            rows={3}
            value={priv.goal}
            maxLength={PANEL_LIMITS.goalChars}
            onValue={panel.setGoal}
            testId="goal-input"
          />
        </section>
      ) : null}

      {showChat ? <AskChat assistant={assistant} /> : null}

      <QuestionsSection panel={panel} assistant={assistant} isHost={isHost} showGoal={showGoal} prepOpener={prepGoals?.opener ?? ""} />

      <section className="bp__section">
        <label className="bp__heading" htmlFor={notesId}>
          Notes
        </label>
        <TextBox
          id={notesId}
          className="bp__textarea bp__textarea--tall"
          rows={8}
          value={priv.notes}
          maxLength={PANEL_LIMITS.privateNotesChars}
          onValue={panel.setNotes}
          testId="notes-input"
        />
      </section>

      {isHost && host?.help?.length ? <HelpSection matches={host.help} onAdd={(text) => panel.setNotes(priv.notes.trim() ? `${priv.notes.replace(/\s+$/, "")}\n${text}` : text)} /> : null}

      <footer className="bp__foot">
        <span className="bp__saved" role="status">
          {panel.saved ? "Saved" : "Saving…"}
        </span>
        <button type="button" className="bp__btn" onClick={() => void copy("mine", priv)}>
          {copied === "mine" ? "Copied" : copied === "failed" ? "Could not copy" : "Copy my notes"}
        </button>
      </footer>
    </>
  );
}

// ---- Ways to help (Mine, host only) ----------------------------------------

/** What the guests said was going wrong, each with something the host could
 * build for it, newest first. Adam, 2026-10-06: tie what they say "to something
 * I can do to help ... display it under my personal notes as the meeting is
 * going." The matches reach this page only as the host's own `assistantHost`
 * message; this section is never rendered for anyone else. */
function HelpSection({ matches, onAdd }: { matches: readonly HelpMatch[]; onAdd: (text: string) => void }): JSX.Element {
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  const shown = [...matches].reverse();
  return (
    <section className="bp__section" aria-label="Ways to help" data-testid="help-matches">
      <h3 className="bp__heading">Ways to help</h3>
      <ul className="bp__list bp-help__list">
        {shown.map((m) => (
          <li key={m.id} className="bp-help__item" data-testid="help-match">
            <p className="bp-help__problem">{m.problem}</p>
            <p className="bp-help__quote">
              {m.who}: {m.quote}
            </p>
            <p className="bp-help__build">{m.build}</p>
            <button
              type="button"
              className="bp__btn bp__btn--ghost bp-help__add"
              disabled={added.has(m.id)}
              onClick={() => {
                onAdd(`${m.problem}: ${m.build}`);
                setAdded((prev) => new Set(prev).add(m.id));
              }}
            >
              {added.has(m.id) ? "Added" : "Add to notes"}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---- Ask Breeze ------------------------------------------------------------

/** A private chat with Breeze. What it answers from is the Room's decision:
 * only this seat's own goal and notes, the Shared panel, and what was said
 * while the call recorded. Nothing here is ever sent to anyone else. */
function AskChat({ assistant }: { assistant: AssistantStore }): JSX.Element {
  const { state, messages, typing, ask } = assistant;
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const off = state?.on !== true;
  const disabled = off;

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, typing]);

  const send = (): void => {
    if (disabled || typing || !draft.trim()) return;
    if (ask(draft)) setDraft("");
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    send();
  };

  return (
    <section className="bp__section bp-ask" aria-label="Ask Breeze" data-testid="ask-chat">
      <label className="bp__heading" htmlFor={inputId}>
        Ask Breeze
      </label>
      {messages.length > 0 || typing ? (
        <div ref={listRef} className="bp-ask__log" role="log" aria-label="Your chat with Breeze" data-testid="ask-log">
          {messages.map((m) => (
            <p
              key={m.id}
              className={`bp-ask__msg bp-ask__msg--${m.from}${m.failed ? " bp-ask__msg--failed" : ""}`}
              data-testid={m.from === "me" ? "ask-mine" : "ask-breeze"}
            >
              {m.text}
            </p>
          ))}
          {typing ? (
            <p className="bp-ask__msg bp-ask__msg--breeze bp-ask__typing" role="status" aria-label="Breeze is typing" data-testid="ask-typing">
              <span />
              <span />
              <span />
            </p>
          ) : null}
        </div>
      ) : null}
      {off ? (
        <p className="bp__empty" data-testid="ask-off">
          Breeze is off.
        </p>
      ) : null}
      <form className="bp-ask__form" onSubmit={onSubmit}>
        <TextBox
          id={inputId}
          className="bp__textarea bp-ask__input"
          rows={1}
          value={draft}
          maxLength={ASK.textChars}
          disabled={disabled}
          onValue={setDraft}
          onEnter={send}
          placeholder="Message Breeze"
          testId="ask-input"
        />
        <button type="submit" className="bp__btn bp__btn--accent bp-ask__send" disabled={disabled || typing || !draft.trim()} data-testid="ask-send">
          Send
        </button>
      </form>
    </section>
  );
}

// ---- Host ------------------------------------------------------------------

function HostTab({ assistant }: { assistant: AssistantStore }): JSX.Element {
  const { state } = assistant;
  const on = state?.on === true;
  const switchId = useId();
  const chatId = useId();
  const notesId = useId();

  return (
    <section className="bp__section" aria-label="Breeze assistant">
      <div className="bp__switch">
        <label className="bp__switch-label" htmlFor={switchId}>
          <span className="bp__heading">Breeze listens and suggests</span>
          <span className="bp__hint">It hears the call only while you record.</span>
        </label>
        <input
          id={switchId}
          type="checkbox"
          role="switch"
          className="bp__toggle"
          checked={on}
          onChange={(e) => assistant.setOn(e.target.checked)}
          data-testid="assistant-switch"
        />
      </div>
      <div className="bp__switch">
        <label className="bp__switch-label" htmlFor={notesId}>
          <span className="bp__heading">Guests can use shared notes</span>
        </label>
        <input
          id={notesId}
          type="checkbox"
          role="switch"
          className="bp__toggle"
          checked={state?.guestNotes === true}
          onChange={(e) => assistant.setOptions({ guestNotes: e.target.checked })}
          data-testid="guest-notes-switch"
        />
      </div>
      {on ? (
        <>
          <div className="bp__switch">
            <label className="bp__switch-label" htmlFor={chatId}>
              <span className="bp__heading">Guests can chat with Breeze</span>
            </label>
            <input
              id={chatId}
              type="checkbox"
              role="switch"
              className="bp__toggle"
              checked={state?.guestChat === true}
              onChange={(e) => assistant.setOptions({ guestChat: e.target.checked })}
              data-testid="guest-chat-switch"
            />
          </div>
        </>
      ) : null}
    </section>
  );
}

// ---- Questions (Mine) --------------------------------------------------------

const seenKey = (): string => `breeze.seenSuggestions.${window.location.pathname}`;

function loadSeen(): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(seenKey());
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function saveSeen(seen: ReadonlySet<string>): void {
  try {
    window.sessionStorage.setItem(seenKey(), JSON.stringify([...seen].slice(-200)));
  } catch {
    // Storage can be blocked; a reload may then offer a question again.
  }
}

const sameText = (t: string): string => t.trim().toLowerCase().replace(/\s+/g, " ");

/** This person's own questions, in one list. Breeze adds the questions it
 * writes for them (the Room sends those to this person's sockets only), and
 * every question can be edited, ticked off, or deleted. */
function QuestionsSection({
  panel,
  assistant,
  isHost,
  showGoal,
  prepOpener,
}: {
  panel: PanelStore;
  assistant: AssistantStore;
  isHost: boolean;
  showGoal: boolean;
  prepOpener: string;
}): JSX.Element {
  const { state } = assistant;
  const [draft, setDraft] = useState("");
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!seen.current) seen.current = loadSeen();
    const marks = seen.current;
    const have = new Set(panel.priv.questions.map((q) => sameText(q.text)));
    let changed = false;
    for (const batch of panel.suggestions) {
      if (batch.audience !== "self") continue;
      for (const item of batch.items) {
        if (item.kind !== "question" || marks.has(item.id)) continue;
        marks.add(item.id);
        changed = true;
        if (have.has(sameText(item.text))) continue;
        have.add(sameText(item.text));
        panel.addQuestion(item.text, "breeze");
      }
    }
    if (changed) saveSeen(marks);
  }, [panel.suggestions, panel]);

  // The opening question Breeze wrote from the prep goes in once; one the
  // host deleted is not put back.
  const opener = isHost ? (assistant.host?.opener || prepOpener).trim() : "";
  useEffect(() => {
    if (!opener) return;
    if (!seen.current) seen.current = loadSeen();
    const mark = `opener:${sameText(opener)}`;
    if (seen.current.has(mark)) return;
    seen.current.add(mark);
    saveSeen(seen.current);
    if (panel.priv.questions.some((q) => sameText(q.text) === sameText(opener))) return;
    panel.addQuestion(opener, "breeze");
  }, [opener, panel]);

  const add = (e: FormEvent): void => {
    e.preventDefault();
    if (!draft.trim()) return;
    panel.addQuestion(draft);
    setDraft("");
  };

  let empty = "No questions yet.";
  if (state?.on && !state.paused) {
    if (isHost) empty = "Breeze adds questions here from your agenda as the call goes.";
    else if (showGoal) empty = panel.priv.goal.trim() ? "Breeze adds questions here as the call goes." : "Add a goal and Breeze will add questions here as the call goes.";
  }

  return (
    <section className="bp__section" aria-label="Questions" data-testid="my-questions">
      <h3 className="bp__heading">Questions</h3>
      {panel.priv.questions.length === 0 ? (
        <p className="bp__empty">{empty}</p>
      ) : (
        <ul className="bp__list" aria-label="Questions">
          {panel.priv.questions.map((q) => (
            <li key={q.id} className={`bp__item${q.done ? " bp__item--done" : ""}`} data-by={q.by ?? "me"}>
              <label className="bp__check">
                <input
                  type="checkbox"
                  checked={q.done}
                  onChange={(e) => panel.updateQuestion(q.id, { done: e.target.checked })}
                  aria-label={`Asked: ${q.text}`}
                />
                <span className="bp__box" aria-hidden="true" />
              </label>
              <div className="bp__main">
                <TextBox
                  className="bp__qedit"
                  rows={1}
                  value={q.text}
                  maxLength={PANEL_LIMITS.itemChars}
                  onValue={(text) => panel.updateQuestion(q.id, { text })}
                  onBlur={(text) => {
                    if (!text.trim()) panel.removeQuestion(q.id);
                  }}
                  singleLine
                  ariaLabel="Question"
                />
                {q.by === "breeze" ? (
                  <span className="bp__meta">
                    <span className="bp__tag bp__tag--breeze">Added by Breeze</span>
                  </span>
                ) : null}
              </div>
              <button type="button" className="bp__icon" aria-label={`Delete: ${q.text}`} onClick={() => panel.removeQuestion(q.id)}>
                <TrashIcon size={19} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="bp__add" onSubmit={add}>
        <TextBox
          className="bp__input"
          value={draft}
          maxLength={PANEL_LIMITS.itemChars}
          onValue={setDraft}
          ariaLabel="Add a question for yourself"
          placeholder="Add a question"
          singleLine
          onEnter={() => {
            if (!draft.trim()) return;
            panel.addQuestion(draft);
            setDraft("");
          }}
        />
        <button type="submit" className="bp__btn bp__btn--accent" disabled={!draft.trim()}>
          Add
        </button>
      </form>
    </section>
  );
}
